import { View } from "./View";
import { QuickSubmitForm } from "../../../components";
import {
  IModalFormProps,
  IPageAndSearchRequest,
  IQueryStringNavLinks,
  ITableHeader,
  IViewTransactionListItem,
  PagedMeta,
} from "../../../interfaces-and-types";
import { IViewPaymentLink } from "../../../interfaces-and-types";
import {
  useQuickSubmitFormHelper,
  useMerchantApiQueryWithParams,
  useReactRouterLink,
  useGetPageQueryParams,
  useQueryString,
} from "../../../hooks";
import { useParams } from "react-router-dom";
import { filterConstants } from "../../../constants";
import { IPaymentQueryMeta } from "../../Payments/Payments";

export const PAYMENTS_BY_PAYLINK_LIST_TABLE_NAME = "filter";

interface ITxsForPaymentLinkContext {
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  transactionsForPaymentLink: IViewTransactionListItem[];
  meta: PagedMeta | undefined;
  headers: ITableHeader[];
  queryStringTabs: IQueryStringNavLinks;
}
export interface IPaymentLinkDetailsModalViewProps extends IModalFormProps {
  modalData: IViewPaymentLink | null;
  navigateToEdit: () => void;
  openDeleteModal: () => void;

  // txs for payment link context
  transactionsForPaymentLinkContext: ITxsForPaymentLinkContext;
}

export const PaymentLinksDetailsModal = () => {
  const { navigate, location, navigateToRoutePreserveParams } =
    useReactRouterLink();

  const closeModal = () => {
    navigateToRoutePreserveParams(
      location.pathname.replace(`/${id}/details`, "")
    );
  };

  const { id } = useParams();

  const {
    isLoading,
    // isSuccess,
    isError,
    data: paymentLink,
  } = useMerchantApiQueryWithParams<{ id: string }, IViewPaymentLink>(
    [
      "getPaymentLinkById",
      {
        id: id!, // NOTE: used to enforce react query cache with correct queryKey
      },
    ],
    {
      enabled: !!id,
    },
    (urlTemplate) => urlTemplate.replace("id", id!)
  );

  //#region Get transactions for the payment link

  const query = useQueryString();
  const status = query.get(
    `${PAYMENTS_BY_PAYLINK_LIST_TABLE_NAME}.${filterConstants.statusKey}`
  );
  const { page, page_size, search } = useGetPageQueryParams({
    queryStringPrefix: PAYMENTS_BY_PAYLINK_LIST_TABLE_NAME,
  });

  const startDate = query.get(filterConstants.startDateKey);
  const endDate = query.get(filterConstants.endDateKey);

  const {
    isLoading: isLoadingTransactions,
    isError: isErrorTransactions,
    isSuccess: isSuccessTransactions,
    data: transactionsForPaymentLink,
    meta: transactionsForPaymentLinkMeta,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest & IPaymentQueryMeta,
    IViewTransactionListItem[],
    PagedMeta
  >([
    "getTransactions",
    {
      page: (page && Number(page)) || 1,
      page_size: (page_size && Number(page_size)) || 10,
      search,
      status: status || "successful",
      filter: {
        payment_link_id: id,
      },
      // start_date: startDate,
      // end_date: endDate,
    },
  ]);

  //#endregion

  const {
    isModalOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,

    isItemDeleteLocked,

    isLoading: isLoadingDeleteItem,
    isSuccess: isSuccessDeleteItem,
    mutate: deleteItem,
  } = useQuickSubmitFormHelper("deletePaymentLink", () => {});

  const navigateToEdit = () => navigate(`/payments/payment-links/${id}/edit`);

  const openPaymentLinkDeleteModal = () =>
    id && openDeleteModal(id, paymentLink?.is_delete_locked || false);

  return (
    <>
      <View
        isLoadingModalData={isLoading}
        isErrorModalData={isError}
        isModalOpen={!!id}
        closeModal={closeModal}
        navigateToEdit={navigateToEdit}
        openDeleteModal={openPaymentLinkDeleteModal}
        modalData={paymentLink || null}
        transactionsForPaymentLinkContext={{
          isLoading: isLoadingTransactions,
          isError: isErrorTransactions,
          isSuccess: isSuccessTransactions,
          transactionsForPaymentLink: transactionsForPaymentLink ?? [],
          meta: transactionsForPaymentLinkMeta,
          headers: [
            {
              key: "amount",
              header: "Amount",
            },
            {
              key: "amount_paid",
              header: "Paid",
            },
            {
              key: "status",
              header: "Status",
            },
            {
              key: "reference",
              header: "Reference",
            },
            {
              key: "customer",
              header: "Customer",
            },
            {
              key: "gateway",
              header: "Provider",
            },
            {
              key: "description",
              header: "Description",
            },
            {
              key: "date",
              header: "Date",
            },
            {
              key: "more",
              header: "",
            },
          ],
          queryStringTabs: {
            query: filterConstants.statusKey,
            options: [
              {
                key: "All",
                value: null,
              },
              {
                key: "Successful",
                value: "successful",
              },
              // {
              //   key: "Partial",
              //   value: "partial",
              // },
              {
                key: "Processing",
                value: "processing",
              },
              {
                key: "Abandoned",
                value: "abandoned",
              },
            ],
          },
        }}
      />

      <QuickSubmitForm
        formTitle="Delete payment link"
        isModalOpen={isDeleteModalOpen}
        closeModal={closeDeleteModal}
        isDeleteLocked={isItemDeleteLocked}
        isSubmitting={isLoadingDeleteItem}
        isSuccess={isSuccessDeleteItem}
        onButtonClick={deleteItem}
        confirmationMessage={
          <>
            <h4>Are you sure you want to delete this payment link?</h4>
            <br />

            <span className="text-danger">
              (NOTE: This action is irreversible)
            </span>
          </>
        }
      />
    </>
  );
};
